import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import {
  clearSetPoint,
  saveSetPoint,
  type ResolvedParameterTarget,
} from '../../services/parameterTargetService';
import { formatTargetNumber } from './formatTarget';

export interface SetPointFormProps {
  aquariumId: string;
  target: ResolvedParameterTarget;
  onSaved: (target: ResolvedParameterTarget, cleared: boolean) => void;
  onCancel: () => void;
}

function parse(raw: string): number | null {
  const value = Number(raw.trim().replace(',', '.'));
  return raw.trim() === '' || !Number.isFinite(value) ? null : value;
}

/**
 * Create, edit or clear the personal set point ± tolerance of one parameter
 * (Reef Guard #1819). Saving applies going forward only — past alerts stay as
 * they were, which the form says explicitly.
 */
export function SetPointForm({ aquariumId, target, onSaved, onCancel }: SetPointFormProps) {
  const { t, i18n } = useTranslation();
  const fmt = (n: number) => formatTargetNumber(n, i18n.language);
  const [setPoint, setSetPoint] = useState(target.setPoint !== null ? String(target.setPoint) : '');
  const [tolerance, setTolerance] = useState(target.tolerance !== null ? String(target.tolerance) : '');
  const [errors, setErrors] = useState<{ setPoint?: string; tolerance?: string }>({});
  const [saveFailed, setSaveFailed] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [busy, setBusy] = useState(false);

  const sp = parse(setPoint);
  const tol = parse(tolerance);
  const preview = sp !== null && tol !== null && sp >= 0 && tol > 0
    ? t('paramTargets.editor.preview', { min: fmt(Math.max(0, sp - tol)), max: fmt(sp + tol), unit: target.unit })
    : null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSaveFailed(false);
    const next: { setPoint?: string; tolerance?: string } = {};
    if (sp === null || sp < 0) next.setPoint = t('paramTargets.editor.setPointError');
    if (tol === null || tol <= 0) next.tolerance = t('paramTargets.editor.toleranceError');
    setErrors(next);
    if (next.setPoint || next.tolerance) {
      document.getElementById(next.setPoint ? 'set-point-value' : 'set-point-tolerance')?.focus();
      return;
    }
    setBusy(true);
    try {
      onSaved(await saveSetPoint(aquariumId, target.parameterId, { set_point: sp as number, tolerance: tol as number }), false);
    } catch {
      setSaveFailed(true);
    } finally {
      setBusy(false);
    }
  }

  async function handleClear(): Promise<void> {
    setSaveFailed(false);
    setBusy(true);
    try {
      onSaved(await clearSetPoint(aquariumId, target.parameterId), true);
    } catch {
      setSaveFailed(true);
    } finally {
      setBusy(false);
    }
  }

  if (confirmClear) {
    return (
      <Stack gap="4" data-testid="set-point-clear-confirm">
        {saveFailed && <Alert tone="danger">{t('paramTargets.editor.saveError')}</Alert>}
        <Text>
          {t('paramTargets.editor.clearConfirm', {
            min: fmt(target.defaultMin),
            max: fmt(target.defaultMax),
            unit: target.unit,
          })}
        </Text>
        <Inline gap="3" justify="end">
          <Button type="button" variant="secondary" onClick={() => setConfirmClear(false)} disabled={busy}>
            {t('paramTargets.editor.cancel')}
          </Button>
          <Button type="button" variant="danger" onClick={() => void handleClear()} disabled={busy}>
            {t('paramTargets.editor.clearConfirmAction')}
          </Button>
        </Inline>
      </Stack>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate data-testid="set-point-form">
      <Stack gap="4">
        {saveFailed && <Alert tone="danger">{t('paramTargets.editor.saveError')}</Alert>}
        <Text color="muted">
          {t('paramTargets.editor.defaultRange', { min: fmt(target.defaultMin), max: fmt(target.defaultMax), unit: target.unit })}
        </Text>
        <Input
          id="set-point-value"
          inputMode="decimal"
          label={t('paramTargets.editor.setPoint', { unit: target.unit })}
          value={setPoint}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSetPoint(e.target.value)}
          error={errors.setPoint}
        />
        <Input
          id="set-point-tolerance"
          inputMode="decimal"
          label={t('paramTargets.editor.tolerance', { unit: target.unit })}
          helperText={t('paramTargets.editor.toleranceHelp')}
          value={tolerance}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setTolerance(e.target.value)}
          error={errors.tolerance}
        />
        {preview && <Text data-testid="set-point-preview">{preview}</Text>}
        <Text color="muted">{t('paramTargets.editor.forwardOnly')}</Text>
        <Inline gap="3" justify="end">
          {target.source === 'set_point' && (
            <Button type="button" variant="ghost" onClick={() => setConfirmClear(true)} disabled={busy}>
              {t('paramTargets.action.clearSetPoint')}
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
            {t('paramTargets.editor.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {t('paramTargets.editor.save')}
          </Button>
        </Inline>
      </Stack>
    </form>
  );
}
